import React from "react";
import { Box, Button } from "@mui/material";
import colors from "../../../shared/themes/colors";
import CameraIcon from "../../../shared/assets/icons/CameraIcon";
import OverflowTooltip from "../../../shared/components/OverflowTooltip";
import { Machine } from "../../machinery/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName, getProductionYearDisplay, getBrandDisplayName, getMachineName, getMachineTypeLabel } from "../../../shared/utils/machineUtils";
import { dt } from "../../../shared/themes/redesignTokens";

interface MachineCardProps {
  machine?: Machine;
  onChoose?: () => void;
  showButton?: boolean;
  isCurrentChoice?: boolean;
}

const MachineCard = ({ machine, onChoose, showButton, isCurrentChoice }: MachineCardProps) => {
  const { translate } = useTranslation(["myMachinery", "common"]);

  if (!machine) {
    return null;
  }

  const isClickable = !!onChoose && !isCurrentChoice;

  return (
    <Box
      className="flex flex-col self-start gap-1"
      onClick={isClickable ? onChoose : undefined}
      sx={{
        width: "100%",
        height: "100%",
        border: isCurrentChoice ? `1px solid ${dt.blue}` : `1px solid ${colors.secondary.gray94}`,
        borderRadius: "8px",
        padding: "16px",
        boxShadow: "0 8px 32px 0 rgba(0, 33, 89, 0.10)",
        display: "flex",
        flexDirection: "column",
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.2s ease",
        ...(isClickable && {
          "&:hover": {
            borderColor: dt.blue,
            boxShadow: "0 12px 40px 0 rgba(0, 33, 89, 0.16)",
          },
        }),
      }}
    >
      <Box className="flex justify-center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100px",
            height: "100px",
            aspectRatio: 1,
            border: "1px solid ${colors.secondary.gray20}",
            backgroundColor: colors.secondary.gray96,
            borderRadius: "8px",
            mt: 4,
          }}
        >
          {(machine?.image?.content || machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl) ? (
            <Box
              component="img"
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "8px",
                border: "1px solid ${colors.secondary.gray94}",
                objectFit: "cover",
              }}
              src={machine?.image?.content || machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl || undefined}
              alt={machine?.model}
            />
          ) : (
            <CameraIcon />
          )}
        </Box>
      </Box>

      <Box className="text-center" sx={{ width: "100%" }}>
        <Box className="text-18-bold max-w-[98%] text-wrap break-words" color={colors.secondary.gray20}>
          {getMachineDisplayName(machine)}
        </Box>
        <Box className="text-12-regular w-full" color={colors.secondary.gray20} sx={{ minHeight: "18px" }}>
          {getMachineName(machine)}
        </Box>
        <Box className="text-12-regular w-full" color={colors.secondary.gray20} sx={{ minHeight: "18px", width: "100%" }}>
          {getProductionYearDisplay(machine)}
        </Box>
      </Box>

      <Box className="flex mt-2" >
        <Box className="text-14-medium w-1/3" color={machine.serialNumber ? colors.secondary.gray20 : colors.primary.red}>
          {translate("myMachinery:sn")}
        </Box>
        <Box className="text-14-medium w-2/3" color={colors.secondary.gray20}>
          <OverflowTooltip text={machine?.serialNumber} />
        </Box>
      </Box>

      <Box className="flex">
        <Box className="text-14-regular w-1/3">
          {translate("myMachinery:machine")}
        </Box>
        <Box className="text-14-regular w-2/3">
          <OverflowTooltip text={"#" + machine?.internalNumber} />
        </Box>
      </Box>

      <Box className="flex mt-2">
        <Box className="text-14-regular w-1/3" color={colors.secondary.gray20}>
          {translate("common:brand")}
        </Box>
        <Box className="w-2/3" sx={{ minHeight: "60px" }}>
          <Box className="text-14-medium" color={colors.secondary.gray20}>
            {getBrandDisplayName(machine)}
          </Box>
          <Box className="text-14-regular">{getMachineTypeLabel(machine?.type)}</Box>
        </Box>
      </Box>

      <Box className="flex mt-1">
        <Box className="text-14-regular w-1/3" color={colors.secondary.gray20}>
          {translate("myMachinery:system")}
        </Box>
        <Box className="w-2/3" sx={{ minHeight: "40px" }}>
          <Box className="text-14-medium" color={colors.secondary.gray20}>
            <OverflowTooltip text={machine?.system ? machine.system : "—"} className="text-14-medium" color={colors.secondary.gray20} />
          </Box>
          <Box className="text-14-regular">
            <OverflowTooltip text={machine?.version ? machine.version : "—"} className="text-14-medium" />
          </Box>
        </Box>
      </Box>

      {showButton && (
        <Box className="flex w-full mt-auto">
          <Button
            fullWidth
            variant={isCurrentChoice ? "contained" : "outlined"}
            color="primary"
            onClick={(e) => { e.stopPropagation(); onChoose?.(); }}
            disabled={isCurrentChoice}
          >
            {isCurrentChoice ? translate("myMachinery:currentChoice") : translate("myMachinery:choose")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MachineCard;
